import type { PaginationState, SortingState } from '@tanstack/react-table'
import { useContext, useMemo } from 'react'

import type { ColumnFilters } from '@/components/filters/column-filter'
import { DataTable, type DataTableProps } from '@/components/data-table/DataTable'
import { Empty } from '@/components/feedback/Empty'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { buildReportColumns, renderCell } from '@/features/report/columns'
import type { TableLayout } from '@/hooks/use-table-preferences'
import { formatFieldValue, type ListWidget } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import type { ReportRow } from '@/services/cube/report'

import { field } from './helpers'
import { InTabs, WidgetCard } from './shared'
import type { ListResult, WidgetStatus } from './types'

/** Canlı liste kontrolleri: sunucu taraflı sayfa/sıra/sütun filtresi + kullanıcı tablo düzeni. */
export interface ListControls {
  pagination: PaginationState
  onPaginationChange: DataTableProps<ReportRow>['onPaginationChange']
  sorting: SortingState
  onSortingChange: DataTableProps<ReportRow>['onSortingChange']
  layout: TableLayout
  onLayoutChange: (layout: TableLayout) => void
  onLayoutReset: () => void
  columnFilters: ColumnFilters
  onColumnFiltersChange: (filters: ColumnFilters) => void
  filterValues: DataTableProps<ReportRow>['filterValues']
  /** Excel'e ekrandaki sayfa değil bütün kayıtlar. */
  fetchExportData: () => Promise<ReportRow[]>
}

const noop = () => {}
const EMPTY_LAYOUT: TableLayout = { columnOrder: [], columnSizing: {}, columnVisibility: {} }

/** Baskıda bir liste en çok bu kadar satır basar; fazlası Excel'e bırakılır. */
export const PRINT_LIST_LIMIT = 1000

/** Baskı: sayfalamasız, araç çubuksuz düz tablo (üst sınır + not). */
function PrintListView({ widget, data }: { widget: ListWidget; data: ListResult | undefined }) {
  const fields = widget.dimensions.filter((item) => item.name !== widget.rowKey)
  const items = data?.items ?? []
  const shown = items.slice(0, PRINT_LIST_LIMIT)
  const total = Math.max(data?.total ?? 0, items.length)
  return (
    <WidgetCard title={widget.title} printMode>
      {shown.length === 0 ? (
        <Empty />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {fields.map((item) => (
                <TableHead key={item.name}>{item.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((row, index) => (
              <TableRow key={index}>
                {fields.map((item) => (
                  <TableCell key={item.name}>{renderCell(item, row[item.name], row)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {total > PRINT_LIST_LIMIT && (
        <p className="text-muted-foreground px-2 py-1 text-xs">
          {messages.documents.runs.print.listTruncated(PRINT_LIST_LIMIT, total)}
        </p>
      )}
    </WidgetCard>
  )
}

/**
 * Saf çizim. `controls` yoksa (donmuş veri) verilen tüm satırlar tek sayfada,
 * sıra/filtre/düzen kalıcılığı olmadan gösterilir. `printMode`: araç çubuğu ve sayfalama yok.
 */
export function ListWidgetView({
  printMode,
  ...props
}: {
  widget: ListWidget
  data: ListResult | undefined
  status?: WidgetStatus
  controls?: ListControls
  printMode?: boolean
}) {
  return printMode ? (
    <PrintListView widget={props.widget} data={props.data} />
  ) : (
    <InteractiveListView {...props} />
  )
}

function InteractiveListView({
  widget,
  data,
  status = {},
  controls,
}: {
  widget: ListWidget
  data: ListResult | undefined
  status?: WidgetStatus
  controls?: ListControls
}) {
  // Sekmede başlık sekmenin kendisi; tablo kart kenarına yapışmasın.
  const inTabs = useContext(InTabs)
  const columns = useMemo(() => buildReportColumns(widget), [widget])
  const items = data?.items
  const staticPagination = useMemo<PaginationState>(
    () => ({ pageIndex: 0, pageSize: Math.max(items?.length ?? 0, 1) }),
    [items],
  )
  const summary = data?.totals?.map((row, index) => (
    <span key={index} className="text-foreground font-medium">
      {messages.report.total}:{' '}
      {widget.totals
        .map((member) => formatFieldValue(field(widget, member), row[member], row))
        .join(' · ')}
    </span>
  ))

  const table = (
    <DataTable
      title={inTabs ? undefined : widget.title}
      tableId={widget.view.toLowerCase()}
      columns={columns}
      data={items ?? []}
      total={data?.total ?? 0}
      pagination={controls?.pagination ?? staticPagination}
      onPaginationChange={controls?.onPaginationChange ?? noop}
      sorting={controls?.sorting ?? []}
      onSortingChange={controls?.onSortingChange ?? noop}
      isLoading={status.isLoading}
      busy={status.busy}
      isError={status.isError}
      error={status.error}
      onRetry={status.onRetry}
      layout={controls?.layout ?? EMPTY_LAYOUT}
      onLayoutChange={controls?.onLayoutChange ?? noop}
      onLayoutReset={controls?.onLayoutReset ?? noop}
      summary={summary}
      columnFilters={controls?.columnFilters}
      onColumnFiltersChange={controls?.onColumnFiltersChange}
      filterValues={controls?.filterValues}
      fetchExportData={controls?.fetchExportData}
    />
  )
  return inTabs ? <div className="p-3">{table}</div> : table
}
